import { addEdge, applyEdgeChanges, applyNodeChanges, Connection, Edge, EdgeChange, Node, NodeChange } from 'reactflow'

import { NodeStep } from '@ui/__generated__/graphql'
import { DatableNodeData, NodeStepPredicate, NodeTypeEnum, PanelEnum } from '@ui/interfaces'
import { nodeDefaultStates } from '@ui/pages/pipelinesWorkflow/components/NodeTypes'
import { useNavigationStateStore } from '@ui/stores/navigation'
import { produce } from 'immer'
import { create } from 'zustand'
import { combine } from 'zustand/middleware'

import { Config } from './NodeStepStore'
import { usePipelineModificationStore } from './PipelineModificationStore'

export type SetNodesType = (nodes: Node<DatableNodeData>[]) => Node[]
export type SetEdgesType = (edges: Edge[]) => Edge[]

interface KeyType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // We use this to bulk update the values in the data object on a node
  [key: string]: string | undefined | boolean | KeyType
}

interface DefaultStateTypes {
  nodes: Node[]
  edges: Edge[]
  modifiedNodes: Node[]
  deletedNodes: string[]
  loafId: string | undefined
  currentNodeId: string | null
  displayModal: PanelEnum
  closePanel: () => void
  loafState: boolean
  loafName: string | undefined
  updatePipeline: boolean
  refreshLayout: boolean
  // Switch between the old and new workflow update
  switchWorkflowUpdate: boolean
}

const combineDefaults: DefaultStateTypes = {
  nodes: [],
  edges: [],
  modifiedNodes: [],
  deletedNodes: [],
  loafId: undefined,
  currentNodeId: null,
  displayModal: PanelEnum.HIDDEN,
  closePanel: () => Promise.resolve(),
  loafState: false,
  loafName: undefined,
  updatePipeline: false,
  refreshLayout: false,
  // Switch between the old and new workflow update
  switchWorkflowUpdate: true,
}

// this is our useStore hook that we can use in our components to get parts of the store and call actions
export const useNodeAndEdgeStore = create(
  combine(combineDefaults, (set, get) => ({
    resetNodesAndEdges: () => {
      set(() => {
        return { ...combineDefaults }
      })
    },
    setupNodesAndEdges: ({
      nodes,
      edges,
      loafId,
      loafName,
      loafState,
    }: {
      nodes: Node[]
      edges: Edge[]
      loafId: string | undefined
      loafName: string | undefined
      loafState: boolean
    }) => {
      set(() => {
        return {
          nodes: nodes,
          edges: edges,
          loafId: loafId,
          loafName: loafName,
          loafState: loafState,
        }
      })
    },
    onNodesChange: (changes: NodeChange[]) => {
      set((state) => {
        return { nodes: applyNodeChanges(changes, state.nodes) }
      })
    },
    onEdgesChange: (changes: EdgeChange[]) => {
      set((state) => {
        return { edges: applyEdgeChanges(changes, state.edges) }
      })
    },
    onConnect: (connection: Connection) => {
      set({
        edges: addEdge(connection, get().edges),
      })
    },
    setNodes: (fn: SetNodesType) => {
      set((state) => {
        const nodes = state.nodes

        const newNodes = fn(nodes)

        usePipelineModificationStore.getState().checkModifications()
        return { nodes: newNodes }
      })
    },
    setEdges: (fn: SetEdgesType) => {
      set((state) => {
        const edges = state.edges

        const newEdges = fn(edges)

        return { edges: newEdges }
      })
    },
    updateIds: (oldId: string, newId: string) => {
      set((state) => {
        const newNodes = state.nodes.map((node) => {
          let newNode: Node = node

          if (node.id === oldId) {
            // it's important to create a new object here, to inform React Flow about the cahnges
            newNode = { ...node, id: newId }
          }

          if (node.data.parentSliceId === oldId) {
            // it's important to create a new object here, to inform React Flow about the cahnges
            newNode = { ...node }
            newNode.data = { ...newNode.data, parentSliceId: newId }
          }
          return newNode
        })

        const newEdges = state.edges.map((edge) => {
          let newEdge: Edge = edge
          if (edge.source === oldId) {
            // it's important to create a new object here, to inform React Flow about the cahnges
            newEdge = { ...edge, source: newId }
          }

          if (edge.target === oldId) {
            // it's important to create a new object here, to inform React Flow about the cahnges
            newEdge = { ...edge, target: newId }
          }

          return newEdge
        })

        return { nodes: newNodes, edges: newEdges }
      })
    },
    setLoafId: (newLoafId: string) => {
      set(() => {
        return { loafId: newLoafId }
      })
    },
    setLoafState: (newState: boolean) => {
      set(() => {
        return { loafState: newState }
      })
    },
    setLoafName: (newName: string) => {
      set(() => {
        return { loafName: newName }
      })
    },
    setCurrentNodeId: (newNodeId: string | null) => {
      useNavigationStateStore.getState().setParams({ nodeId: newNodeId })

      set(() => {
        return { currentNodeId: newNodeId }
      })
    },
    setDisplayModal: (newState: PanelEnum) => {
      set(() => {
        return { displayModal: newState }
      })
    },
    setClosePanel: (newClosePanelCall: () => void) => {
      set(() => {
        return { closePanel: newClosePanelCall }
      })
    },
    setModifiedNodes: (newModifiedNodes: Node[]) => {
      set(() => {
        const modifiedNodes = get().modifiedNodes
        return { modifiedNodes: [...modifiedNodes, ...newModifiedNodes] }
      })
    },
    setDeletedNodes: (newDeletedNodes: Node[]) => {
      set(() => {
        const deletedNodeIds = newDeletedNodes.map((node: Node) => node.id)
        return { deletedNodes: [...get().deletedNodes, ...deletedNodeIds] }
      })
    },
    onNodesDelete: (nodes: Node[]) => {
      set(() => {
        const newlyDeletedNodeIds: string[] = []

        const nodeIds = get().nodes.map((node: Node) => node.id)
        nodes.forEach((node) => {
          if (nodeIds.includes(node.id)) {
            newlyDeletedNodeIds.push(node.id)
          }
        })

        return { deletedNodes: [...get().deletedNodes, ...newlyDeletedNodeIds] }
      })
    },
    updateNodeType: (nodeId: string, newType: NodeTypeEnum) => {
      set(
        produce((state) => {
          // Because I'm a dunce, all the enums are capitalised
          const nodeType = NodeTypeEnum[newType]

          // Find the placehodler node, and change it to our type
          // We also shove in the default values for that new type.
          // In the future if we let people change the node type at a later stage,
          // we can run into issues where there's additional data from a previous type's default state
          const node = state.nodes.find((node: Node) => node.id === nodeId)
          node.type = nodeType
          Object.keys(nodeDefaultStates[nodeType] || {}).forEach((key) => {
            if (!nodeDefaultStates[nodeType]) return

            node.data[key] = nodeDefaultStates[nodeType][key]
          })
        })
      )
    },
    updateNodeKeyValue: (nodeId: string | null, obj: KeyType) => {
      set(
        produce((state) => {
          if (!nodeId) return

          const node = state.nodes.find((node: Node) => node.id === nodeId)
          Object.keys(obj).forEach((key) => {
            node.data[key] = obj[key]
          })
        })
      )
    },
    updateNodeStepPredicate: (stepId: string, predicate: NodeStepPredicate) => {
      set((state) => ({
        nodes: state.nodes.map((node) => {
          if (node.type !== NodeTypeEnum.TRANSFORM) return node

          const updatedSteps = node.data.steps.map((step: NodeStep) => {
            if (step.id === stepId) {
              return { ...step, predicate }
            }
            return step
          })
          return { ...node, data: { ...node.data, steps: updatedSteps } }
        }),
      }))
      set(() => ({ refreshLayout: true }))
    },
    updateNodeStepConfig: (stepId: string, config: Partial<Config>) => {
      set((state) => ({
        nodes: state.nodes.map((node) => {
          if (node.type !== NodeTypeEnum.TRANSFORM) return node

          const updatedSteps = node.data.steps.map((step: NodeStep) => {
            if (step.id === stepId) {
              return { ...step, config }
            }
            return step
          })
          return { ...node, data: { ...node.data, steps: updatedSteps } }
        }),
      }))
      set(() => ({ refreshLayout: true }))
    },
    getCurrentNodeKey: (key: string) => {
      const state = get()
      if (!state.currentNodeId) return
      const node = state.nodes.find((node: Node) => node.id === state.currentNodeId)
      if (!node) return

      return node.data[key]
    },
    getHasBeenModified: () => {
      const state = get()
      if (!state.loafId) return false
      const node = state.nodes.find((node: Node) => node.data.modified)
      if (node) return true
    },
    setUpdatePipeline: (newState: boolean) => {
      set(() => {
        return { updatePipeline: newState }
      })
    },
    setRefreshLayout: (newState: boolean) => {
      set(() => {
        return { refreshLayout: newState }
      })
    },
    // Switch between the old and new workflow update
    setSwitchWorkflowUpdate: (newState: boolean) => {
      set(() => {
        return { switchWorkflowUpdate: newState }
      })
    },
  }))
)
